import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';
import { get } from '@ember/object';

// ---------------------------------------------------------------------------
// IconBadge – colored icon badge for lists, cards, and section headers
// ---------------------------------------------------------------------------

/**
 * Color theme options for the icon badge.
 *
 * All 15 names are preserved for API back-compat with existing tio-employee
 * + tio-admin consumers, but they resolve to **6 TIO canonical visual
 * variants** via the `.app-icon-badge--<color>` CSS modifiers — see
 * `addons/tio-reimagined/src/styles.css`. Visual collapses:
 *
 *   gray                                        → neutral
 *   emerald, green                              → success
 *   teal, cyan, sky, blue, indigo               → info (teal)
 *   violet, purple                              → brand (purple)
 *   pink, rose, red                             → danger
 *   amber, orange                               → warning
 *
 * If you need finer visual differentiation, the right answer is to add a
 * TIO token (not to extend this palette).
 */
export type IconBadgeColor =
  | 'gray'
  | 'emerald'
  | 'green'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'rose'
  | 'red'
  | 'amber'
  | 'orange';

/**
 * Size options for the icon badge
 */
export type IconBadgeSize = 'sm' | 'md' | 'lg';

interface IconBadgeSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Color theme for background and icon */
    color?: IconBadgeColor;
    /** Size variant */
    size?: IconBadgeSize;
  };
  Element: HTMLDivElement;
}

/**
 * Per-size icon dimensions. The badge's padding + radius come from the
 * `.app-icon-badge--<size>` CSS modifier; only the inner phosphor icon
 * size has to be picked here at the call site.
 */
const ICON_SIZES: Record<IconBadgeSize, number> = {
  sm: 16,
  md: 20,
  lg: 24,
};

/**
 * IconBadge - A colored icon badge for lists and cards
 *
 * Small presentational component for displaying a Phosphor icon with a
 * colored background. Used throughout as a building block for navigation
 * cards, section panels, and list items.
 *
 * Built on the `.app-icon-badge*` semantic class family — the color
 * modifier on the wrapping div sets `--icon-badge-bg / --icon-badge-fg`
 * from the canonical TIO recipes, and the badge background + phosphor
 * icon color compose against them.
 *
 * @example Basic usage
 * ```gts
 * import IconBadge from 'tio-reimagined/components/icon-badge';
 * import { PhTrophy } from 'ember-phosphor-icons';
 *
 * <IconBadge @icon={{PhTrophy}} @color="violet" />
 * ```
 *
 * @example Large size
 * ```gts
 * <IconBadge @icon={{PhGraduationCap}} @color="emerald" @size="lg" />
 * ```
 */
const IconBadge: TOC<IconBadgeSignature> = <template>
  <div
    data-test-icon-badge
    class="app-icon-badge app-icon-badge--{{if @color @color 'gray'}}
      app-icon-badge--{{if @size @size 'md'}}"
    aria-hidden="true"
    ...attributes
  >
    {{#let @icon as |Icon|}}
      <Icon @size={{if @size (get ICON_SIZES @size) ICON_SIZES.md}} />
    {{/let}}
  </div>
</template>;

export default IconBadge;
export type { IconBadgeSignature };
